import { ref } from 'vue'

import { defineStore } from 'pinia'

import * as aggr from '@/api-generator/aggr'
import { feCompaniesAPI } from '@/APIs'

export interface FetchOrgSortTagsOptions {
  /** 成功後的回調函數 */
  onSuccess?: (data: aggr.FECompanyQueryOrganizationTagsRs[]) => void | Promise<void>
  /** 錯誤後的回調函數 */
  onError?: (error: any) => void
  /** 是否自動更新 store（預設為 true） */
  updateStore?: boolean
}

export const useOrgSortTagStore = defineStore('orgSortTag', () => {
  const $reset = () => {
    orgSortTags.value = null
  }

  const orgSortTags = ref<aggr.FECompanyQueryOrganizationTagsRs[] | null>(null)
  const setOrgSortTags = (tags: aggr.FECompanyQueryOrganizationTagsRs[]) => {
    orgSortTags.value = tags
  }

  /**
   * 獲取組織的分類標籤
   * @param params 查詢參數
   * @param options 選項（callback、錯誤處理、是否更新 store）
   * @returns Promise<{ data, error }>
   *
   * @example
   * // 使用 callback
   * await fetchOrgSortTags(params, {
   *   onSuccess: (data) => console.log('成功', data)
   * })
   *
   * @example
   * // 使用 .then
   * fetchOrgSortTags(params).then(({ data }) => {
   *   if (data) console.log('成功', data)
   * })
   *
   * @example
   * // 簡單調用
   * await fetchOrgSortTags(params)
   */
  const fetchOrgSortTags = async (params: aggr.FECompanyQueryOrganizationTagsRq, options?: FetchOrgSortTagsOptions) => {
    const { onSuccess, onError, updateStore = true } = options || {}

    try {
      const { data } = await feCompaniesAPI.queryOrganizationTags(params)

      if (updateStore) {
        setOrgSortTags(data)
      }

      if (onSuccess) {
        await onSuccess(data)
      }

      return { data, error: null }
    } catch (error) {
      console.error('Failed to fetch org sort tags:', error)

      if (onError) {
        onError(error)
      }

      return { data: null, error }
    }
  }

  return {
    $reset,
    orgSortTags,
    setOrgSortTags,
    fetchOrgSortTags
  }
})
